import React, { useCallback, useEffect, useState } from "react";
import { Check, CreditCard } from "lucide-react";
import adminPricingService, { PricingPlan } from "../../../services/adminPricingService";
import paymentService, {
  PaymentCancelledError,
  PaymentRecord,
  PlanAccessStatus,
} from "../../../services/paymentService";
import { useAuth } from "../../../contexts/AuthContext";
import SuccessModal from "../../Common/SuccessModal";
import ErrorModal from "../../Common/ErrorModal";
import "./Plans.css";

const Plans: React.FC = () => {
  const { user } = useAuth();

  const [plans, setPlans] = useState<PricingPlan[]>([]);
  const [payments, setPayments] = useState<PaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [payingPlanId, setPayingPlanId] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [access, setAccess] = useState<PlanAccessStatus | null>(null);

  const loadAccount = useCallback(async () => {
    try {
      const [history, status] = await Promise.all([paymentService.getMyPayments(), paymentService.getStatus()]);
      setPayments(history);
      setAccess(status);
    } catch {
      // history/status are secondary; the plans stay usable without them
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    adminPricingService
      .getPublicPlans()
      .then((planList) => {
        if (!cancelled) setPlans(planList);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : "Could not load plans");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadAccount();
  }, [loadAccount]);

  const handlePay = async (plan: PricingPlan) => {
    setPayingPlanId(plan.id);
    try {
      const { planName } = await paymentService.payForPlan(plan.id, {
        name: user?.personName,
        email: user?.mailId || user?.email,
        contact: user?.phoneNumber,
      });
      setSuccessMessage(`Your payment for the ${planName} plan was successful and the plan is now active.`);
      loadAccount();
    } catch (err) {
      if (!(err instanceof PaymentCancelledError)) {
        setErrorMessage(err instanceof Error ? err.message : "Payment failed. Please try again.");
        loadAccount();
      }
    } finally {
      setPayingPlanId(null);
    }
  };

  const accessSummary = (a: PlanAccessStatus): string => {
    const parts: string[] = [];
    if (a.periodEndsAt) parts.push(`${a.planName} active until ${formatDate(a.periodEndsAt)}`);
    if (a.creditsRemaining > 0) {
      parts.push(`${a.creditsRemaining} single-auction credit${a.creditsRemaining > 1 ? "s" : ""} left`);
    }
    return parts.length ? parts.join(" · ") : "No active plan.";
  };

  const formatDate = (value: string | null) =>
    value ? new Date(value.replace(" ", "T")).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "-";

  return (
    <div className="ap-plans-wrapper">
      <div className="ap-plans-header">
        <CreditCard className="ap-plans-header-icon" />
        <div>
          <h1>Plans &amp; Billing</h1>
          <p>Choose a plan and pay securely with Razorpay.</p>
        </div>
      </div>

      {access && (
        <div className={`ap-plans-access ${access.hasAccess ? "ap-plans-access-ok" : "ap-plans-access-none"}`}>
          {accessSummary(access)}
        </div>
      )}

      {loading && <p className="ap-plans-muted">Loading plans...</p>}
      {loadError && <p className="ap-plans-error">{loadError}</p>}

      <div className="ap-plans-grid">
        {plans.map((plan) => {
          return (
            <div key={plan.id} className="ap-plan-card">
              {plan.badge && <span className="ap-plan-badge">{plan.badge}</span>}
              <h3>{plan.name}</h3>
              {plan.tagline && <p className="ap-plans-muted">{plan.tagline}</p>}
              <div className="ap-plan-price">
                <strong>{plan.price}</strong>
                <span>{plan.period}</span>
              </div>
              <ul>
                {plan.features.map((feature) => (
                  <li key={feature}>
                    <Check size={16} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                type="button"
                className="ap-plan-pay-btn"
                disabled={payingPlanId !== null}
                onClick={() => handlePay(plan)}
              >
                {payingPlanId === plan.id ? "Processing..." : "Pay now"}
              </button>
            </div>
          );
        })}
      </div>

      <h2 className="ap-plans-subtitle">Payment history</h2>
      {payments.length === 0 ? (
        <p className="ap-plans-muted">No payments yet.</p>
      ) : (
        <div className="ap-plans-table-wrap">
          <table className="ap-plans-table">
            <thead>
              <tr>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
                <th>Payment ID</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td data-label="Plan">{p.plan_name}</td>
                  <td data-label="Amount">₹{Number(p.amount).toLocaleString("en-IN")}</td>
                  <td data-label="Status">
                    <span className={`ap-plan-status ap-plan-status-${p.status}`}>
                      {p.status === "created" ? "pending" : p.status}
                    </span>
                  </td>
                  <td data-label="Date">{formatDate(p.paid_at || p.created_at)}</td>
                  <td data-label="Payment ID">{p.razorpay_payment_id || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SuccessModal
        isOpen={successMessage !== null}
        onClose={() => setSuccessMessage(null)}
        title="Payment successful"
        message={successMessage || ""}
      />
      <ErrorModal
        isOpen={errorMessage !== null}
        onClose={() => setErrorMessage(null)}
        title="Payment failed"
        message={errorMessage || ""}
      />
    </div>
  );
};

export default Plans;
